import type {
  LockerverseAuction as Auction,
  LockerverseAuctionItem as AuctionItem,
  LockerverseAuctionStatus,
} from "@lockerverse/sdk/auctions";
import { ArrowRight, Clock3, Gavel, ShoppingBag } from "lucide-react";
import {
  CatalogImage,
  Metric,
  STATUS_LABELS,
  Timing,
} from "./auction-catalog-parts.tsx";
import { AuctionDescription } from "./auction-description.tsx";
import {
  formatAuctionMoney as formatMoney,
  getAuctionDisplayStatus,
  isAuctionItemSoldOut,
} from "./auction-display.ts";
import type { LockerverseStyle, LockerverseTheme } from "./theme.ts";
import { useAuctionBoundary } from "./use-auction-boundary.ts";

export type LockerverseAuctionItemCardProps = {
  auction: Auction;
  item: AuctionItem;
  onSelect: () => void;
  theme?: LockerverseTheme;
  /** Applied to the scoped card container. */
  className?: string;
  style?: LockerverseStyle;
  showDescription?: boolean;
  showMetrics?: boolean;
  showTiming?: boolean;
};
function itemCardDisplay(item: AuctionItem, status: LockerverseAuctionStatus) {
  if (item.saleMode === "buy_now") {
    const soldOut = isAuctionItemSoldOut(item);
    const availabilityLabel =
      status === "active" ? "Buy now" : STATUS_LABELS[status];
    const label = soldOut ? "Sold out" : availabilityLabel;
    return {
      chip: label,
      cta: soldOut
        ? "Sold out"
        : { active: "Buy now", draft: "Preview item", finished: "View item" }[
            status
          ],
      price: formatMoney(item.buyNowPriceAmount ?? 0),
      priceLabel: "Buy Now",
      secondary:
        item.availableQuantity === null
          ? "Available"
          : String(item.availableQuantity),
    };
  }
  return {
    chip: status === "active" ? "Live" : STATUS_LABELS[status],
    cta: { active: "Bid now", draft: "Preview item", finished: "View item" }[
      status
    ],
    price:
      status === "finished" && !item.maxBidAmount
        ? "No bids"
        : formatMoney(item.maxBidAmount ?? 0),
    priceLabel: status === "finished" ? "Final bid" : "Current",
    secondary: formatMoney(item.minBidAmount ?? 0),
  };
}
export function LockerverseAuctionItemCard({
  auction,
  item,
  onSelect,
  theme = "dark",
  className,
  style,
  showDescription = true,
  showMetrics = true,
  showTiming = true,
}: LockerverseAuctionItemCardProps) {
  useAuctionBoundary(
    item.startAt ?? auction.startAt,
    item.endAt ?? auction.endAt
  );
  const status = getAuctionDisplayStatus(auction, item);
  const buyNow = item.saleMode === "buy_now";
  const display = itemCardDisplay(item, status);
  return (
    <div
      className={[
        "lockerverse-auctions",
        "lockerverse-auctions--card",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      data-lockerverse-theme={theme}
      style={style}
    >
      <article className="lockerverse-auctions__catalog-card">
        <button
          aria-label={`View ${item.name}`}
          className="lockerverse-auctions__catalog-select"
          onClick={onSelect}
          type="button"
        />
        <div className="lockerverse-auctions__catalog-media">
          <CatalogImage
            fallback={
              item.image ||
              auction.configuration.image ||
              auction.community?.banner ||
              auction.community?.thumbnail
            }
            images={item.images}
            name={item.name}
          />
          <div className="lockerverse-auctions__catalog-chips">
            <span
              className="lockerverse-auctions__catalog-chip"
              data-live={status === "active"}
            >
              {display.chip}
            </span>
            {showTiming ? (
              <Timing auction={auction} item={item} status={status} />
            ) : null}
          </div>
          {showMetrics ? (
            <dl className="lockerverse-auctions__catalog-metrics">
              <Metric
                icon={
                  buyNow ? (
                    <ShoppingBag aria-hidden="true" size={16} />
                  ) : (
                    <Gavel aria-hidden="true" size={16} />
                  )
                }
                label={display.priceLabel}
                value={display.price}
              />
              <Metric
                icon={<Clock3 aria-hidden="true" size={16} />}
                label={buyNow ? "Available" : "Floor"}
                value={display.secondary}
              />
            </dl>
          ) : null}
        </div>
        <div className="lockerverse-auctions__catalog-body">
          <h3>{item.name}</h3>
          {showDescription ? (
            <AuctionDescription
              className="lockerverse-auctions__catalog-description"
              description={item.description || "View auction item details."}
            />
          ) : null}
          <div className="lockerverse-auctions__catalog-footer lockerverse-auctions__catalog-footer--item">
            <span className="lockerverse-auctions__catalog-cta">
              {display.cta}
              <ArrowRight aria-hidden="true" size={16} />
            </span>
          </div>
        </div>
      </article>
    </div>
  );
}
